import React, { useState, useEffect } from "react"
import { Menu, Modal, Tooltip } from "antd"
import { notify } from "shared/ui/Notifications/notify"
import { Editor as TiptapEditor } from "@tiptap/react"

import { useEngine } from "hooks/api/useEngine"
import { useTemplates } from "features/QuestionnaireEditor/api/useTemplates"
import { canApplyFormatting } from "utils/editorUtils"

import items from "./MenuItems"
import actions from "./MenuActions"

import DataQButton from "shared/ui/Buttons/DataQButton.component"
import ProgressModal from "shared/ui/ProgressModal/ProgressModal.component"
import ExportToDecipherModal from "./ExportToDecipher/ExportToDecipherModal.component"
import AIConversionModal from "./AIConversion/AIConversionModal.component"
import ExportMapSourceFillModal from "features/QueryConsole/components/ExportMap/ExportMapSourceFillModal.component"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faArrowRightToBracket,
  faListOl,
  faUndo,
  faRedo,
  faSitemap,
  faPlus,
  faHighlighter,
  faComments,
  faBold,
  faItalic,
  faBook,
} from "@fortawesome/free-solid-svg-icons"

import { useProjectStore, Project } from "stores/projectStore"
import TemplatesModal from "./Templates/TemplatesModal.component"
import ExportModal from "shared/ui/ExportModal/ExportModal.component"
import { Variant } from "types/questionnaire.types"
import {
  EXPORT_VARIABLE_TYPES,
  VARIABLE_STYLE,
  ConversionType,
  ConversionOptions,
} from "types/engineExport.types"
import { TemplateFileListResult } from "types/template.types"
import { SelectOption, MenuClickEvent, UI_INPUT_TYPES } from "types/ui.types"
import { DOCS_LINKS } from "constants/docsLinks"

// Define the props interface for QuestionnaireMenuBar
interface QuestionnaireMenuBarProps {
  /** Indicates if the template should be used in the master configuration */
  inMaster: boolean
  /** Indicates if the questionnaire is empty */
  qnrEmpty: boolean
  /** The editor instance used to manipulate the questionnaire */
  editor: TiptapEditor | null
  /** Indicates if the highlighting is enabled */
  highlightingEnabled: boolean
  /** Function to toggle the highlighting feature */
  toggleHighlighting: () => void
  /** The variants data that will be passed to the export form */
  variants: Variant[]
  /** Function to check the questionnaire */
  checkQuestionnaire: (id: string) => Promise<void>
  /** Function to retrieve the notes for the questionnaire */
  getQuestionnaireNotes: () => string[]
  /** Function to toggle the line numbers in the editor */
  toggleLineNumbers: () => void
  /** Indicates if the line numbers are enabled */
  lineNumbersEnabled: boolean
  /** Function to toggle the element highlighting in the editor */
  toggleElementHighlighting: () => void
  /** Indicates if the element highlighting is enabled */
  elementHighlightingEnabled: boolean
  /** Function to toggle the structure panel */
  toggleStructurePanel?: () => void
  /** Indicates if the structure panel is visible */
  structurePanelVisible?: boolean
  /** Function to toggle the add element modal */
  toggleAddElementModal?: () => void
  /** Indicates if the add element modal is visible */
  addElementModalVisible?: boolean
  /** Function to refresh element highlighting */
  refreshHighlight?: () => void
  /** Ref to structure panel button for tour */
  structurePanelButtonRef?: React.RefObject<HTMLButtonElement | null>
  /** Ref to add element button for tour */
  addElementButtonRef?: React.RefObject<HTMLButtonElement | null>
  /** Ref to element highlight button for tour */
  elementHighlightButtonRef?: React.RefObject<HTMLButtonElement | null>
  /** Whether the editor is connecting to collaboration server */
  isConnecting?: boolean
  /** Function to toggle the comments panel */
  toggleCommentsPanel?: () => void
  /** Indicates if the comments panel is visible */
  commentsPanelVisible?: boolean
  /** Number of comments in the current document */
  commentsCount?: number
  /** Indicates new comments arrived via websocket */
  hasNewComments?: boolean
  /** Variant currently being edited ("" / undefined = MASTER) */
  currentVariant?: string
  /** Saves the questionnaire (the editor's manual save) */
  onSave?: () => Promise<void>
}

// Define interfaces for export functions
interface DatamapExportValues {
  template: boolean
  groupSections: boolean
  variableNames: EXPORT_VARIABLE_TYPES
  variableStyle: VARIABLE_STYLE
  includeQuestionText: boolean
}

interface QuestionnaireExportValues {
  languages: string[]
  variant: string
  type: ConversionType
  variableNames: EXPORT_VARIABLE_TYPES
  style: string
}

// Define template files structure based on API response
type TemplateFiles = TemplateFileListResult

/**
 * Functional component representing the menu bar for the questionnaire editor.
 *
 * @component
 * @param props - The component props
 * @returns The rendered menu bar component
 */
const QuestionnaireMenuBar: React.FC<QuestionnaireMenuBarProps> = (props) => {
  const project: Project = useProjectStore((state) => state.project)
  const {
    inMaster,
    qnrEmpty,
    editor,
    highlightingEnabled,
    toggleHighlighting,
    variants,
    checkQuestionnaire,
    getQuestionnaireNotes,
    toggleLineNumbers,
    lineNumbersEnabled,
    toggleElementHighlighting,
    elementHighlightingEnabled,
    toggleStructurePanel,
    structurePanelVisible,
    toggleAddElementModal,
    addElementModalVisible,
    refreshHighlight,
    structurePanelButtonRef,
    addElementButtonRef,
    elementHighlightButtonRef,
    isConnecting = false,
    toggleCommentsPanel,
    commentsPanelVisible,
    hasNewComments = false,
    currentVariant,
    onSave,
  } = props

  // Typed state variables
  const [isExportDecipherModalVisible, setExportDecipherModalVisible] =
    useState<boolean>(false)
  const [isTemplateModalVisible, setTemplateModalVisible] =
    useState<boolean>(false)
  const [isExportDatamapModalVisible, setIsExportDatamapModalVisible] =
    useState<boolean>(false)
  const [
    isExportQuestionnaireModalVisible,
    setIsExportQuestionnaireModalVisible,
  ] = useState<boolean>(false)
  const [isAIConversionModalVisible, setIsAIConversionModalVisible] =
    useState<boolean>(false)
  const [isSourceFillModalVisible, setIsSourceFillModalVisible] =
    useState<boolean>(false)
  const [sourceFillCsv, setSourceFillCsv] = useState<string>("")
  const [templateFiles, setTemplateFiles] = useState<TemplateFiles>({
    public: [],
    private: [],
  })

  const {
    downloadDatamapCSV,
    isDownloadingDatamapCSV,
    exportToHTML,
    getDatamapCSV,
  } = useEngine()
  const { listTemplateFiles, listingTemplateFiles } = useTemplates()

  // State to track if formatting (bold/italic) is allowed at current cursor position
  const [formattingAllowed, setFormattingAllowed] = useState<boolean>(true)

  // Update formatting allowed state when editor selection changes
  useEffect(() => {
    if (!editor) return

    const updateFormattingState = () => {
      setFormattingAllowed(canApplyFormatting(editor))
    }

    // Initial check
    updateFormattingState()

    // Listen for selection and content changes
    editor.on("selectionUpdate", updateFormattingState)
    editor.on("update", updateFormattingState)

    return () => {
      editor.off("selectionUpdate", updateFormattingState)
      editor.off("update", updateFormattingState)
    }
  }, [editor])

  /**
   * Opens "Fill export map from source document".
   *
   * Fetches the TEMPLATE datamap (VARIABLE column and category codes empty) and
   * hands it to the fill modal in standalone mode: no collected data is needed,
   * so this runs before fieldwork and ends in a saved export map profile.
   *
   * The datamap is generated server-side from the SAVED questionnaire — any
   * pending editor changes must be saved before this is called.
   */
  const openSourceFill = async (): Promise<void> => {
    const hideLoading = notify.toast.loading("Preparing export map template...")
    try {
      const response = await getDatamapCSV(
        project.id,
        false, // no section prefix: imported data paths are ungrouped by default, and the map must match them
        EXPORT_VARIABLE_TYPES.NUMBERING,
        undefined, // variable style: the datamap dialog's setting is per-export
        true, // question text: the fill matches on the original wording when present
        currentVariant || undefined,
        true // template
      )
      const raw =
        typeof response.data === "string"
          ? response.data
          : String(response.data ?? "")
      setSourceFillCsv(raw)
      setIsSourceFillModalVisible(true)
    } catch (error) {
      notify.toast.error("Export map template could not be created!")
      console.error("Error creating export map template:", error)
    } finally {
      hideLoading()
    }
  }

  /**
   * After inserting an AI conversion: offer to carry the codes the converter
   * read from the document straight into the export map.
   *
   * @param info - What the conversion captured
   */
  const handleConversionInserted = (info: {
    sourceCodesCaptured: number
  }): void => {
    if (info.sourceCodesCaptured <= 0) return

    Modal.confirm({
      title: "Fill the export map now?",
      content: `Source codes were captured from the document (${info.sourceCodesCaptured} questions). The questionnaire will be saved first, then the export map can be filled from those codes and saved as a profile.`,
      okText: "Save and fill",
      cancelText: "Later",
      onOk: async () => {
        // Order matters: the template datamap is generated from the SAVED
        // questionnaire, so the just-inserted content must be persisted first.
        if (onSave) await onSave()
        await openSourceFill()
      },
    })
  }

  /**
   * Handles the click event on menu items.
   *
   * @param e - The click event object
   */
  const onClick = async (e: MenuClickEvent): Promise<void> => {
    const actionKey = e.key as keyof ReturnType<typeof actions>
    const actionMap = actions(editor)
    const action = actionMap[actionKey]

    if (e.key === "template") {
      try {
        setTemplateModalVisible(true)
        const templateRes = await listTemplateFiles()
        setTemplateFiles(templateRes.data.result)
      } catch (error) {
        notify.toast.error("Template failed to be parsed!")
        console.error("Error parsing template:", error)
      }
    } else if (e.key === "export_to_decipher_xml") {
      setExportDecipherModalVisible(true)
    } else if (e.key === "export_data_map") {
      setIsExportDatamapModalVisible(true)
    } else if (e.key === "fill_export_map_source") {
      await openSourceFill()
    } else if (e.key === "export_questionnaire") {
      setIsExportQuestionnaireModalVisible(true)
    } else if (e.key === "convert_questionnaire_ai") {
      setIsAIConversionModalVisible(true)
    } else if (action && typeof action === "function") {
      action()
    }
  }

  /**
   * TODO Refactor this function to be in a helper file with same function in ExportToDecipherOptionsForm.component.jsx
   * Extracts unique languages from an array of variants and a project object.
   *
   * @param variants - An array of variant objects
   * @param project - A project object containing languages
   * @returns An array of unique languages
   */
  const getUniqueLanguages = (
    variants: Variant[],
    project: Project
  ): string[] => {
    const variantLanguages: string[] = variants.flatMap(
      (variant: Variant) => variant.languages
    )
    const allLanguages: string[] = [project.Designlanguage, ...variantLanguages]
    return [...new Set(allLanguages)]
  }

  // Default to MASTER if variants array is empty, or if only one language, default it
  const variantOptions: SelectOption[] =
    variants.length > 0
      ? variants.map((variant: Variant) => ({
          label: variant.name,
          value: variant.name,
        }))
      : [{ label: "MASTER", value: "MASTER" }]

  const languageOptions: SelectOption[] = getUniqueLanguages(
    variants,
    project
  ).map((language: string) => ({
    label: language,
    value: language,
  }))

  /**
   * Confirms the export of a data map template.
   *
   * @param filename - The name of the file for the export
   * @param additionalValues - Additional export options
   * @returns Promise that resolves when export is complete
   */
  const confirmDatamapExport = async (
    filename: string,
    additionalValues: Record<string, unknown>
  ): Promise<void> => {
    try {
      const values = additionalValues as unknown as DatamapExportValues
      await downloadDatamapCSV(
        project.id,
        filename,
        values.template,
        values.groupSections,
        values.variableNames,
        values.variableStyle,
        values.includeQuestionText
      )
      setIsExportDatamapModalVisible(false)
      notify.toast.success("Datamap created successfully!")
    } catch (error) {
      notify.toast.error("Datamap failed to be created!")
      console.error("Error creating datamap:", error)
    }
  }

  /**
   * Confirms the export of Questionnaire.
   *
   * @param filename - The name of the file for the export
   * @param additionalValues - Additional options for the export (language, variant and type)
   * @returns Promise that resolves when export is complete
   */
  const confirmQuestionnaireExport = async (
    filename: string,
    additionalValues: Record<string, unknown>
  ): Promise<void> => {
    const values = additionalValues as unknown as QuestionnaireExportValues
    const { type, variableNames, ...otherOptions } = values

    // Show loading message
    const hideLoading = notify.toast.loading(`Exporting questionnaire...`)

    try {
      // Create ConversionOptions object with required filename
      const options: ConversionOptions = {
        filename,
        ...otherOptions,
      }

      await exportToHTML(project.id, options, filename, type, variableNames)
      hideLoading()
      setIsExportQuestionnaireModalVisible(false)
      notify.toast.success("Questionnaire Exported successfully!")
    } catch (error) {
      hideLoading()
      notify.toast.error("Questionnaire failed to be created!")
      console.error("Error creating Questionnaire:", error)
    }
  }

  return (
    <>
      <ExportModal
        title="Export Data Map"
        confirm={confirmDatamapExport}
        cancel={setIsExportDatamapModalVisible}
        isVisible={isExportDatamapModalVisible}
        message="Please enter a filename for your data map export"
        additionalInputs={[
          {
            name: "template",
            type: UI_INPUT_TYPES.CHECKBOX,
            checkboxLabel: "Only Export Template",
            required: false,
          },
          {
            name: "groupSections",
            type: UI_INPUT_TYPES.CHECKBOX,
            checkboxLabel: "Group Questionnaire Sections",
            required: false,
          },
          {
            name: "variableNames",
            type: UI_INPUT_TYPES.SELECT,
            label: "Use as variable names",
            multiple: false,
            defaultValue: "numbering",
            options: [
              { value: "numbering", label: "Numbered automatically" },
              { value: "labels", label: "Generate from questionnaire labels" },
            ],
            required: false,
            disabled: (values: Record<string, unknown>) =>
              values.template === true,
          },
          {
            name: "variableStyle",
            type: UI_INPUT_TYPES.SELECT,
            label: "Variable style",
            multiple: false,
            defaultValue: VARIABLE_STYLE.DEFAULT,
            options: [
              { value: VARIABLE_STYLE.DEFAULT, label: "Default (s/p prefixes)" },
              { value: VARIABLE_STYLE.DECIPHER, label: "Decipher standard (r/c prefixes)" },
            ],
            required: false,
          },
          {
            name: "includeQuestionText",
            type: UI_INPUT_TYPES.CHECKBOX,
            checkboxLabel: "Include full question text",
            required: false,
          },
        ]}
      />

      <ExportModal
        title="Export Questionnaire"
        confirm={confirmQuestionnaireExport}
        cancel={setIsExportQuestionnaireModalVisible}
        isVisible={isExportQuestionnaireModalVisible}
        message="Please enter a filename for your questionnaire export"
        additionalInputs={[
          {
            name: "languages",
            type: UI_INPUT_TYPES.SELECT,
            label: "Languages",
            multiple: true,
            defaultValue:
              languageOptions.length === 1
                ? [languageOptions[0].value]
                : undefined,
            options: languageOptions,
            required: true,
          },
          {
            name: "variant",
            type: UI_INPUT_TYPES.SELECT,
            label: "Variant",
            multiple: false,
            defaultValue:
              variantOptions.length === 1 ? variantOptions[0].value : undefined,
            options: variantOptions,
            required: true,
          },
          {
            name: "type",
            type: UI_INPUT_TYPES.SELECT,
            label: "Export Type",
            multiple: false,
            defaultValue: "word",
            options: [
              { value: "html", label: "HTML" },
              { value: "word", label: "Word" },
            ],
            required: true,
          },
          {
            name: "variableNames",
            type: UI_INPUT_TYPES.SELECT,
            label: "Use as variable names",
            multiple: false,
            defaultValue: "numbering",
            options: [
              { value: "numbering", label: "Numbered automatically" },
              { value: "labels", label: "Generate from questionnaire labels" },
            ],
            required: false,
            disabled: (values: Record<string, unknown>) =>
              values.template === true,
          },
          {
            name: "style",
            type: UI_INPUT_TYPES.SELECT,
            label: "Style",
            multiple: false,
            defaultValue: "default",
            options: [{ value: "default", label: "Default DATA(Q)" }],
          },
          {
            name: "notes",
            type: UI_INPUT_TYPES.SELECT,
            label: "Include Notes",
            multiple: true,
            defaultValue: [...new Set(getQuestionnaireNotes())],
            options: [...new Set(getQuestionnaireNotes())].map((note) => ({
              value: note,
              label: note,
            })),
          },
        ]}
      />

      <ProgressModal
        title="Creating Datamap"
        isLoading={isDownloadingDatamapCSV}
        duration={100}
      />

      <div className="flex flex-row w-full border">
        <ExportToDecipherModal
          getQuestionnaireNotes={getQuestionnaireNotes}
          isExportDecipherModalVisible={isExportDecipherModalVisible}
          setExportDecipherModalVisible={setExportDecipherModalVisible}
          project={project}
          variants={variants}
        />

        <TemplatesModal
          inMaster={inMaster}
          qnrEmpty={qnrEmpty}
          isTemplateModalVisible={isTemplateModalVisible}
          setTemplateModalVisible={setTemplateModalVisible}
          listingTemplateFiles={listingTemplateFiles}
          templateFiles={templateFiles}
          editor={editor}
        />

        <AIConversionModal
          isVisible={isAIConversionModalVisible}
          onClose={() => setIsAIConversionModalVisible(false)}
          projectId={project.id}
          editor={editor}
          onInserted={handleConversionInserted}
        />

        <ExportMapSourceFillModal
          open={isSourceFillModalVisible}
          onClose={() => setIsSourceFillModalVisible(false)}
          projectId={project.id}
          mode="standalone"
          initialCsv={sourceFillCsv}
        />

        <Menu
          className="border-gray-300 w-full"
          onClick={onClick}
          selectedKeys={[]}
          mode="horizontal"
          items={items}
          disabled={isConnecting}
        />

        <div
          className={`flex ml-auto border-b ${
            isConnecting
              ? "pointer-events-none opacity-50"
              : "pointer-events-auto"
          }`}
        >
          <Tooltip title="Undo (Ctrl+Z)" placement="bottomRight">
            <div className="flex">
              <DataQButton
                type="secondary"
                className="w-7 !h-7 !p-0 my-auto mr-2"
                onClick={() => {
                  editor?.chain().focus().undo().run()
                  refreshHighlight?.()
                }}
                isDisabled={isConnecting || !editor?.can().undo()}
              >
                <FontAwesomeIcon icon={faUndo} />
              </DataQButton>
            </div>
          </Tooltip>

          <Tooltip title="Redo (Ctrl+Y / Ctrl+Shift+Z)" placement="bottomRight">
            <div className="flex">
              <DataQButton
                type="secondary"
                className="w-7 !h-7 !p-0 my-auto mr-2"
                onClick={() => {
                  editor?.chain().focus().redo().run()
                  refreshHighlight?.()
                }}
                isDisabled={isConnecting || !editor?.can().redo()}
              >
                <FontAwesomeIcon icon={faRedo} />
              </DataQButton>
            </div>
          </Tooltip>

          <div className="border-l border-gray-300 h-7 my-auto mr-2"></div>

          <Tooltip
            title={
              formattingAllowed
                ? "Bold (Ctrl+B)"
                : "Formatting not allowed in labels"
            }
            placement="bottomRight"
          >
            <div className="flex">
              <DataQButton
                type={editor?.isActive("bold") ? "primary" : "secondary"}
                className="w-7 !h-7 !p-0 my-auto mr-2"
                onClick={() =>
                  formattingAllowed &&
                  editor?.chain().focus().toggleBold().run()
                }
                isDisabled={isConnecting || !formattingAllowed}
              >
                <FontAwesomeIcon icon={faBold} />
              </DataQButton>
            </div>
          </Tooltip>

          <Tooltip
            title={
              formattingAllowed
                ? "Italic (Ctrl+I)"
                : "Formatting not allowed in labels"
            }
            placement="bottomRight"
          >
            <div className="flex">
              <DataQButton
                type={editor?.isActive("italic") ? "primary" : "secondary"}
                className="w-7 !h-7 !p-0 my-auto mr-2"
                onClick={() =>
                  formattingAllowed &&
                  editor?.chain().focus().toggleItalic().run()
                }
                isDisabled={isConnecting || !formattingAllowed}
              >
                <FontAwesomeIcon icon={faItalic} />
              </DataQButton>
            </div>
          </Tooltip>

          <div className="border-l border-gray-300 h-7 my-auto mr-2"></div>

          <Tooltip title="Toggle Highlighting" placement="bottomRight">
            <div className="flex">
              <DataQButton
                type={highlightingEnabled ? "primary" : "secondary"}
                className="w-7 !h-7 !p-0 my-auto mr-2"
                onClick={() => toggleHighlighting()}
                isDisabled={isConnecting}
              >
                (Q)
              </DataQButton>
            </div>
          </Tooltip>

          <Tooltip title="Toggle Element Highlighting" placement="bottomRight">
            <div className="flex">
              <DataQButton
                ref={
                  elementHighlightButtonRef as React.RefObject<HTMLButtonElement>
                }
                type={elementHighlightingEnabled ? "primary" : "secondary"}
                className="w-7 !h-7 !p-0 my-auto mr-2"
                onClick={() => toggleElementHighlighting()}
                isDisabled={isConnecting}
              >
                <FontAwesomeIcon icon={faHighlighter} />
              </DataQButton>
            </div>
          </Tooltip>

          <Tooltip title="Toggle Line Numbers" placement="bottomRight">
            <div className="flex">
              <DataQButton
                type={lineNumbersEnabled ? "primary" : "secondary"}
                className="w-7 !h-7 !p-0 my-auto mr-2"
                onClick={() => toggleLineNumbers()}
                isDisabled={isConnecting}
              >
                <FontAwesomeIcon icon={faListOl} />
              </DataQButton>
            </div>
          </Tooltip>

          <Tooltip
            title="Structure Panel (Ctrl+Shift+E)"
            placement="bottomRight"
          >
            <div className="flex">
              <DataQButton
                ref={
                  structurePanelButtonRef as React.RefObject<HTMLButtonElement>
                }
                type={structurePanelVisible ? "primary" : "secondary"}
                className="w-7 !h-7 !p-0 my-auto mr-2"
                onClick={() => toggleStructurePanel?.()}
                isDisabled={isConnecting || qnrEmpty}
              >
                <FontAwesomeIcon icon={faSitemap} />
              </DataQButton>
            </div>
          </Tooltip>

          <Tooltip title="Add Survey Element" placement="bottomRight">
            <div className="flex">
              <DataQButton
                ref={addElementButtonRef as React.RefObject<HTMLButtonElement>}
                type={addElementModalVisible ? "primary" : "secondary"}
                className="w-7 !h-7 !p-0 my-auto mr-2"
                onClick={() => toggleAddElementModal?.()}
                isDisabled={isConnecting || qnrEmpty}
              >
                <FontAwesomeIcon icon={faPlus} />
              </DataQButton>
            </div>
          </Tooltip>

          <Tooltip title="Check Questionnaire" placement="bottomRight">
            <div className="flex">
              <DataQButton
                type="secondary"
                className="w-7 !h-7 !p-0 my-auto mr-2"
                onClick={() => checkQuestionnaire(project.id)}
                isDisabled={isConnecting}
              >
                <FontAwesomeIcon icon={faArrowRightToBracket} />
              </DataQButton>
            </div>
          </Tooltip>

          <Tooltip title="Format guide documentation" placement="bottomRight">
            <div className="flex">
              <DataQButton
                type="secondary"
                className="w-7 !h-7 !p-0 my-auto mr-2"
                onClick={() => window.open(DOCS_LINKS.FORMAT_GUIDE, "_blank")}
                isDisabled={isConnecting}
              >
                <FontAwesomeIcon icon={faBook} />
              </DataQButton>
            </div>
          </Tooltip>

          <div className="border-l border-gray-300 h-7 my-auto mr-2"></div>

          <Tooltip title="Comments Panel" placement="bottomRight">
            <div className="relative mr-2 my-auto">
              <DataQButton
                type={commentsPanelVisible ? "primary" : "secondary"}
                className="w-7 !h-7 !p-0"
                onClick={() => toggleCommentsPanel?.()}
                isDisabled={isConnecting}
              >
                <FontAwesomeIcon icon={faComments} />
              </DataQButton>
              {/* New comments indicator */}
              {hasNewComments && !commentsPanelVisible && (
                <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-red-500 rounded-full border border-white pointer-events-none" />
              )}
            </div>
          </Tooltip>
        </div>
      </div>
    </>
  )
}

export default QuestionnaireMenuBar
